import {vec2, vec4, mat4, vec3, glMatrix} from "gl-matrix";
import * as WebGLUtils from "%COMMON/WebGLUtils"
import {ShaderLocationsVault} from "%COMMON/ShaderLocationsVault";
import {VertexAttrib} from "VertexAttrib";


export class View {
    private readonly gl: WebGLRenderingContext;
    private shaderProgram: WebGLShader;
    private shaderLocations: ShaderLocationsVault;
    private outerCircleRadius: number;
    private innerCircleRadius: number;
    private proj: mat4;
    private readonly outerCircleCenter: vec2;
    private time: number;
    private seedPointR: number;
    private seedPointTheta: number;
    private showCurve: boolean;
    private readonly numPoints: number = 360;
    private circleVbo: WebGLBuffer;
    private pathVbo: WebGLBuffer;
    private readonly colorOuter: vec4 = vec4.fromValues(1, 0, 0, 0);
    private readonly colorInner: vec4 = vec4.fromValues(1, 1, 0, 0);
    private readonly colorDot: vec4 = vec4.fromValues(0, 0, 1, 0);
    private readonly colorPath: vec4 = vec4.fromValues(0, 1, 0, 0);
    private readonly dotRadius: number = 20;
    private pathCount: number = 0;

    constructor(gl: WebGLRenderingContext) {
        this.gl = gl;
        this.outerCircleRadius = 400;
        this.outerCircleCenter = vec2.fromValues(0, 0);
        this.innerCircleRadius = 345;
        this.time = 0;
        this.seedPointR = 120;
        this.seedPointTheta = 0;
        this.showCurve = false;
    }


    /**
     * This function sets up the vbo's and their vertices to be drawn
     * @param vShaderSource The vertex shader source
     * @param fShaderSource The fragment shader source
     */
    public init(vShaderSource: string, fShaderSource: string): void {
        //create and set up the shader
        this.shaderProgram = WebGLUtils.createShaderProgram(this.gl, vShaderSource, fShaderSource);
        //now get all the shader variables from it
        this.shaderLocations = new ShaderLocationsVault(this.gl, this.shaderProgram);
        this.gl.useProgram(this.shaderProgram);

        const unitCircle = this.genUnitCircle(this.numPoints);
        const path = this.drawDotPath();

        this.circleVbo = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.circleVbo);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, unitCircle, this.gl.STATIC_DRAW);

        this.pathVbo = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.pathVbo);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, path, this.gl.DYNAMIC_DRAW);

        //set the clear color
        this.gl.clearColor(0, 0, 0, 1);

        this.proj = mat4.ortho(mat4.create(),
            -1.5 * this.outerCircleRadius, 1.5 * this.outerCircleRadius,
            -1.5 * this.outerCircleRadius, 1.5 * this.outerCircleRadius, 100, -100);
        this.gl.viewport(0, 0, 400, 400);

    }

    /**
     * This function generates the points for a unit circle
     * The first element of the returned {@link Float32Array} is the center point. Offset by one vertex if you want to
     * draw a line
     * @param numPoints the number of vertices on the diameter to draw.
     * @returns a {@link Float32Array} array of vertices containiig numPoinis + 1 vertices where the zeroth vertex is
     * the center point
     * @private
     */
    private genUnitCircle(numPoints: number): Float32Array {
        const vertexArr: Array<number> = [];
        vertexArr.push(0, 0); // this is setting the center of the circle
        for (let i = 0; i < numPoints; i++) {
            const angle: number = glMatrix.toRadian(((i + 1) / numPoints) * 360);
            vertexArr.push(Math.cos(angle), Math.sin(angle));
        }
        return new Float32Array(vertexArr);
    }

    /**
     * This function handles increasing the time and calling draw
     */
    public animate(): void {
        this.time = (this.time + 1) % Number.MAX_VALUE;
        // console.log("Time: "+this.time);
        this.draw();
    }


    /**
     * This function handles drawing all the vertices to the screen. Also applies transformations.
     */
    public draw(): void {
        //clear the window
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
        this.gl.uniformMatrix4fv(this.shaderLocations.getUniformLocation('proj'), false, this.proj);
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.circleVbo);
        this.gl.vertexAttribPointer(this.shaderLocations.getAttribLocation('vPosition'), 2, this.gl.FLOAT, false, 8, 0);
        this.gl.enableVertexAttribArray(this.shaderLocations.getAttribLocation('vPosition'));

        // draw outer circle
        this.drawCircleLine(vec3.fromValues(this.outerCircleCenter[0], this.outerCircleCenter[1], 0), vec3.fromValues(this.outerCircleRadius, this.outerCircleRadius, 1), this.colorOuter, 0);

        // draw inner circle
        //const innerCenterX = ((this.outerCircleRadius - this.innerCircleRadius) + this.outerCircleCenter[0]) * Math.cos(glMatrix.toRadian(this.time));
        //const innerCenterY = ((this.outerCircleRadius - this.innerCircleRadius) + this.outerCircleCenter[0]) * Math.sin(glMatrix.toRadian(this.time));
        const innerCircleCenterX = ((this.outerCircleRadius - this.innerCircleRadius) + this.outerCircleCenter[0])
        this.drawCircleLine(vec3.fromValues(innerCircleCenterX, 0, 0), vec3.fromValues(this.innerCircleRadius, this.innerCircleRadius, 0), this.colorInner, glMatrix.toRadian(this.time));

        // draw dot
        this.drawCircleSolid(glMatrix.toRadian(this.time), vec3.fromValues(innerCircleCenterX, 0, 0), vec3.fromValues(this.dotRadius, this.dotRadius, 0), this.colorDot);

        // draw path
        if (this.showCurve) {
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.pathVbo);
            this.gl.vertexAttribPointer(this.shaderLocations.getAttribLocation('vPosition'), 2, this.gl.FLOAT, false, 8, 0);
            this.gl.enableVertexAttribArray(this.shaderLocations.getAttribLocation('vPosition'));
            this.gl.uniformMatrix4fv(this.shaderLocations.getUniformLocation('modelView'), false, mat4.create())
            this.gl.uniform4fv(this.shaderLocations.getUniformLocation('vColor'), this.colorPath);
            this.gl.drawArrays(this.gl.LINE_LOOP, 0, this.pathCount);
        }
    }

    /**
     * This function handles re-generating the path of the spirograph when any of the parameters is changed
     * @private
     */
    private updatePath() {
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.pathVbo);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, this.drawDotPath(), this.gl.DYNAMIC_DRAW);
    }

    /**
     * This function handles applying transformations and drawing a circle line
     * @param translate a {@link vec3} containing the translation values
     * @param scale a {@link vec3} containing the scale values
     * @param color a {@link vec4} containing the color values
     * @param angle a number **in radians** to rotate the circle by
     * @private
     */
    private drawCircleLine(translate: vec3, scale: vec3, color: vec4, angle: number): void {
        let modelView = mat4.create();
        angle !== 0 && mat4.rotateZ(modelView, modelView, angle)
        mat4.translate(modelView, modelView, translate);
        mat4.scale(modelView, modelView, scale);
        this.gl.uniform4fv(this.shaderLocations.getUniformLocation('vColor'), color);
        this.gl.uniformMatrix4fv(this.shaderLocations.getUniformLocation('modelView'), false, modelView);

        this.gl.drawArrays(this.gl.LINE_LOOP, 1, this.numPoints);
    }

    /**
     * This function applies translations and draws a circle as a solid object
     * @param rotAngle the angle to rotate the circle **in radians**
     * @param translate a {@link vec3} containing the translation values
     * @param scale a {@link vec3} containing the scale values
     * @param color a {@link vec4} containing the color values
     * @private
     */
    private drawCircleSolid(rotAngle: number, translate: vec3, scale: vec3, color: vec4): void {
        let modelView = mat4.create();
        this.dotTransformations(modelView, translate, scale, rotAngle);
        this.gl.uniform4fv(this.shaderLocations.getUniformLocation('vColor'), color);
        this.gl.uniformMatrix4fv(this.shaderLocations.getUniformLocation('modelView'), false, modelView);

        this.gl.drawArrays(this.gl.TRIANGLE_FAN, 0, this.numPoints + 1);
    }


    /**
     * This method creates the transformation matrix for the inner circle dot given the following paramters
     * @param modelView a {@link mat4} to apply the transformation to
     * @param translate a {@link vec3} containing the translation values per axis
     * @param scale a {@link vec3} containing the scale values per axis
     * @param rotAngle The angle **in radians** to rotate the dot
     * @returns the {@link mat4} passed in
     * @private
     */
    private dotTransformations(modelView: mat4, translate: vec3, scale: vec3, rotAngle: number): mat4 {
        mat4.rotateZ(modelView,modelView,rotAngle)
        mat4.translate(modelView, modelView, translate);
        mat4.rotateZ(modelView, modelView, (glMatrix.toRadian(this.seedPointTheta) + (this.outerCircleRadius / this.innerCircleRadius) * -rotAngle ));
        mat4.translate(modelView, modelView, vec3.fromValues(this.seedPointR, 0, 0));
        mat4.scale(modelView, modelView, scale);
        return modelView;
    }

    /**
     * This method creates the vertices for the path drawn by the dot in the spirograph
     * @returns a {@link Float32Array} containing the path vertices
     * @private
     */
    private drawDotPath(): Float32Array {
        let modelView: mat4;
        const posInit: vec4 = vec4.fromValues(0, 0, 0, 1);
        const vertexArr: Array<number> = [];
        let firstPoint: vec2;
        let broken: boolean = false;
        const innerCircleCenterX = ((this.outerCircleRadius - this.innerCircleRadius) + this.outerCircleCenter[0])
        for (let i = 0; i < 360 * 50; i++) {
            modelView = mat4.create();
            this.dotTransformations(modelView, vec3.fromValues(innerCircleCenterX, 0, 0), vec3.fromValues(this.dotRadius, this.dotRadius, 0), glMatrix.toRadian(i))
            const posTransformed = vec4.create();
            vec4.transformMat4(posTransformed, posInit, modelView)
            i === 0 && (firstPoint = vec2.fromValues(posTransformed[0], posTransformed[1]));
            vertexArr.push(posTransformed[0], posTransformed[1]);
            if (i > 10 && vec2.distance(firstPoint, vec2.fromValues(posTransformed[0], posTransformed[1])) <= 0.01) {
                this.pathCount = i + 1;
                broken = true;
                break;
            }
        }

        !broken && (this.pathCount = 360 * 50);
        return new Float32Array(vertexArr);
    }

    public toggleShowCurve(): void {
        this.showCurve = !this.showCurve;
        this.draw();
    }

    public getOuterRadius(): number {
        return this.outerCircleRadius;
    }

    public getInnerRadius(): number {
        return this.innerCircleRadius;
    }

    public getSeedPointRadius(): number {
        return this.seedPointR;
    }

    public getSeedPointTheta(): number {
        return this.seedPointTheta;
    }

    public incrementOuterRadius(): void {
        this.outerCircleRadius += 5;
        this.time = 0;
        this.proj = mat4.ortho(mat4.create(),
            -1.5 * this.outerCircleRadius, 1.5 * this.outerCircleRadius,
            -1.5 * this.outerCircleRadius, 1.5 * this.outerCircleRadius, 100, -100);
        this.updatePath();
    }

    public decrementOuterRadius(): void {
        if (this.outerCircleRadius > 5) {
            this.outerCircleRadius -= 5;
            this.time = 0;
            this.proj = mat4.ortho(mat4.create(),
                -1.5 * this.outerCircleRadius, 1.5 * this.outerCircleRadius,
                -1.5 * this.outerCircleRadius, 1.5 * this.outerCircleRadius, 100, -100);
            this.updatePath();
        }

    }

    public incrementInnerRadius(): void {
        if (this.innerCircleRadius + 5 < this.outerCircleRadius) {
            this.innerCircleRadius += 5;
            this.time = 0;
            this.updatePath();
        }
    }

    public decrementInnerRadius(): void {
        if (this.innerCircleRadius > 5) {
            this.innerCircleRadius -= 5;
            if (this.seedPointR > this.innerCircleRadius) {
                this.seedPointR = this.innerCircleRadius;
            }
            this.time = 0;
            this.updatePath();
        }

    }

    public incrementSeedPointRadius(): void {
        if (this.seedPointR < this.innerCircleRadius) {
            this.seedPointR += 1;
            this.updatePath();
        }
    }

    public decrementSeedPointRadius(): void {
        if (this.seedPointR > 1) {
            this.seedPointR -= 1;
            this.time = 0;
            this.updatePath();
        }
    }

    public incrementSeedPointTheta(): void {
        if (this.seedPointTheta < 360) {
            this.seedPointTheta += 5;
            this.time = 0;
            this.updatePath();
        }
    }

    public decrementSeedPointTheta(): void {
        if (this.seedPointTheta > 1) {
            this.seedPointTheta -= 5;
            this.time = 0;
            this.updatePath();
        }
    }
}
